'use client';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { Button } from '@/app/components/ui/button';
import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from '@/app/components/ui/popover';
import { cn } from '@/app/utils';
import type { TourItem } from './types';

interface TourPopoverProps {
  open: boolean;
  tourItem: TourItem | undefined;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  /** 是否显示底部控制区 */
  showFooter?: boolean;
  currentStep: number;
  totalSteps: number;
  nextButtonText?: string;
  prevButtonText?: string;
  onClose?: () => void;
  onNext?: () => void;
  onPrev?: () => void;
}

export const TourPopover: FC<TourPopoverProps> = ({
  open,
  tourItem,
  anchorRef,
  showFooter,
  currentStep,
  totalSteps,
  nextButtonText,
  prevButtonText,
  onClose,
  onNext,
  onPrev,
}) => {
  const t = useTranslations();
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setPortalContainer(document.body);
  }, []);

  if (!portalContainer || !open || !tourItem) return null;
  const isLastStep = currentStep === totalSteps - 1;
  const showPrevButton = currentStep > 0;
  const showStepIndicator = totalSteps > 3;

  return (
    <Popover open={true}>
      {/* 锚点元素 */}
      {createPortal(
        <PopoverAnchor asChild>
          <div ref={anchorRef} className="pointer-events-none fixed" />
        </PopoverAnchor>,
        portalContainer,
      )}

      {/* 弹窗内容 */}
      <PopoverContent
        // 使用 z-40 让 tour 永远位于 Dialog（z-50）之下，避免引导气泡盖住开屏弹窗等更高优先级的全局弹窗
        className={cn(
          'group z-40 w-[250px] overflow-visible rounded-8 border-0 p-0 shadow-lg',
          tourItem.popoverCtxClassName,
        )}
        align={tourItem.align || 'start'}
        side={tourItem.side}
        sideOffset={tourItem?.sideOffset || 12}
        collisionPadding={{
          top: 24,
          bottom: 24,
          left: 16,
          right: 16,
        }}
        data-tour-popover="true"
      >
        <div
          className={cn(
            'rounded-8 bg-gradient-to-b from-[#0062FF] to-system-blue02 p-3',
            tourItem.popoverCtxInnerClassName,
          )}
        >
          {/* 标题区 */}
          <div
            className={cn(
              'flex items-center gap-1 font-medium text-base text-system-white',
              (tourItem.icon || tourItem.title) && 'mb-1',
            )}
          >
            {tourItem.icon}
            {tourItem.title}
            <BaseLineClose
              className="absolute top-4 right-3 cursor-pointer text-base text-system-white"
              onClick={onClose}
            />
          </div>

          {/* 描述内容 */}
          {tourItem.description && (
            <div className={cn('whitespace-pre-wrap text-sm text-system-white leading-[140%]', tourItem.descClassName)}>
              {tourItem.description.map((item, index) => {
                if (typeof item === 'string') {
                  return <span key={`desc-${index}-${item.slice(0, 10)}`}>{item}</span>;
                }
                const { text, style } = item;
                return (
                  <span key={`desc-${index}-${text.slice(0, 10)}`} style={style}>
                    {text}
                  </span>
                );
              })}
            </div>
          )}

          {/* 自定义内容 */}
          {tourItem.content}

          {/* 底部控制区 */}
          {showFooter && (
            <div className="mt-4 flex w-full items-center justify-between">
              {/* 步骤指示器 */}
              <div className="text-sm text-white opacity-60">
                {showStepIndicator && (
                  <div>
                    {currentStep + 1} <span className="opacity-[.27]">/</span> {totalSteps}
                  </div>
                )}
              </div>

              {/* 控制按钮 */}
              <div className="flex items-center gap-2">
                {showPrevButton && (
                  <Button variant="text" size="original" className="font-normal text-xs" onClick={onPrev}>
                    {prevButtonText || t('create.tour.prev')}
                  </Button>
                )}
                <Button
                  size="original"
                  className="rounded-full bg-system-white12 px-6 py-2 text-system-white text-xs hover:bg-system-white24"
                  onClick={onNext}
                >
                  {nextButtonText || (isLastStep ? t('create.tour.done') : t('create.tour.next'))}
                </Button>
              </div>
            </div>
          )}
        </div>
        <PopoverArrow
          className={cn(
            'group-data-[side=right]:group-data-[align=end]:text-[#017FFF] group-data-[side=bottom]:text-[#0163FF] group-data-[side=left]:text-[#0169FF] group-data-[side=right]:text-[#0169FF] group-data-[side=top]:text-[#0185FF]',
            tourItem.arrowClassName,
          )}
        />
      </PopoverContent>
    </Popover>
  );
};
